import type { Reducer } from "@reduxjs/toolkit";
import Aviator from "aviator";
import { mergeRight, omit } from "ramda";
import { type UnknownAction, combineReducers } from "redux";

import { LOG_OUT } from "actions/authentication";
import { getBaseUri } from "get_base_uri";
import {
  saveOrderAddressToLocalStorage,
  saveReorderToLocalStorage,
} from "helpers/local_storage";
import {
  APPLY_ORDER_ADDRESS_FROM_LOCAL_STORAGE,
  UPDATE_CONTACT_INFO,
  UPDATE_ORDER_STATUS,
} from "packages/online_ordering/actions/checkout";
import {
  CLEAR_CHECKOUT_FORM_ERRORS,
  RETURN_CHECKOUT_FORM_ERRORS,
  VALIDATE_CONTACT_FIELD_ON_BLUR,
} from "packages/online_ordering/actions/common";
import { UPDATE_CONSUMER_INFO } from "packages/online_ordering/actions/consumer";
import { REORDER } from "packages/online_ordering/actions/ordering_options";
import type { OloCreditCard } from "packages/online_ordering/components/objects/OloCreditCard";
import addresses from "packages/online_ordering/reducers/addresses";
import type { OloOrder } from "packages/online_ordering/types/OloOrder";
import cards from "reducers/credit_cards";

export interface ConsumerState {
  addresses: AddressState;
  cards: OloCreditCard[];
  contactInfo: ContactInfoState;
  orders: OloOrder[];
}

export interface ContactInfoState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  phoneNumbers: Array<{
    uuid: string;
    phoneNumber: string;
  }>;
  address: Address;
  addresses: Address[];
  textAlerts: boolean;
  errors: Record<string, string> | null;
}

export interface Address {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

export interface AddressState {
  existing: Address[];
  editable: Address[];
}

const initialState: ContactInfoState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  textAlerts: true,
  phoneNumbers: [], // Alternate phone numbers
  address: {
    line1: "",
    line2: "",
    city: "",
    state: "",
    zip: "",
  },
  addresses: [],
  errors: null,
};

function contactInfo(
  state: ContactInfoState = initialState,
  action
): ContactInfoState {
  switch (action.type) {
    case UPDATE_CONSUMER_INFO: {
      const contactDetails = omit(["cards", "orders"], action.consumerInfo);
      return {
        ...state,
        ...contactDetails,
      };
    }

    case UPDATE_CONTACT_INFO: {
      let updatedContactInfo = {
        ...state,
        ...action.contactInfo,
      };

      if (action.contactInfo.address) {
        updatedContactInfo = {
          ...state,
          ...action.contactInfo,
          address: {
            ...state.address,
            ...action.contactInfo.address,
          },
        };
      }

      saveOrderAddressToLocalStorage(updatedContactInfo.address);

      return updatedContactInfo;
    }

    case APPLY_ORDER_ADDRESS_FROM_LOCAL_STORAGE: {
      const { address } = action;

      return {
        ...state,
        address,
      };
    }

    case VALIDATE_CONTACT_FIELD_ON_BLUR: {
      if (action.error) {
        return {
          ...state,
          errors: mergeRight(state.errors ?? {}, action.error),
        };
      }

      return {
        ...state,
        errors: omit([action.field], state.errors ?? {}),
      };
    }

    case RETURN_CHECKOUT_FORM_ERRORS: {
      return {
        ...state,
        errors: action.contactFormErrors,
      };
    }

    case CLEAR_CHECKOUT_FORM_ERRORS: {
      return {
        ...state,
        errors: null,
      };
    }

    case UPDATE_ORDER_STATUS: {
      if (action.status === "success") {
        return initialState;
      }
      return state;
    }

    case LOG_OUT: {
      return initialState;
    }

    default:
      return state;
  }
}

function orders(state: OloOrder[] = [], action): OloOrder[] {
  switch (action.type) {
    case UPDATE_CONSUMER_INFO: {
      if (action.consumerInfo.orders) {
        return action.replace
          ? action.consumerInfo.orders
          : [...state, ...action.consumerInfo.orders];
      }

      return state;
    }

    case REORDER: {
      saveReorderToLocalStorage(action.items);
      Aviator.navigate(getBaseUri());
      return state;
    }

    case LOG_OUT: {
      return [];
    }

    default:
      return state;
  }
}

const consumerReducers = combineReducers({
  // TODO implement the types when you need to touch them
  addresses: addresses as Reducer<AddressState, UnknownAction, AddressState>,
  contactInfo,
  // TODO implement the types when you need to touch them
  cards,
  orders,
});

export default consumerReducers;
